import {
  Button,
  DelayedRender,
  InteractiveContainer,
  MultiSectionLayout,
} from "@jugl-web/ui-components";
import { ProgressMessages } from "@jugl-web/ui-kit/feedback/ProgressMessages";
import { cx } from "@jugl-web/utils";
import { useDialogState } from "@jugl-web/utils/hooks/useDialogState";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChatBotUIProduct, ChatBotUIProps, ChatBotUIRef } from "../../types";
import { ChatBotInfoMessageComponent } from "./components/ChatBotInfoMessageComponent";
import { ChatBotMessageComponent } from "./components/ChatBotMessageComponent";
import { ChatBotProductDetailsDrawer } from "./components/ChatBotProductDetailsDrawer";
import { ChatFooter } from "./components/ChatFooter";
import { ChatHeader } from "./components/ChatHeader";
import { ChatInput } from "./components/ChatInput";
import {
  FloatingAlertManager,
  type FloatingAlertManagerHandle,
} from "./components/FloatingAlertManager";
import { TypingIndicatorBubble } from "./components/TypingIndicatorBubble";

export const ChatBotUI = forwardRef<ChatBotUIRef, ChatBotUIProps>(
  (
    {
      messages,
      suggestions = [],
      config,
      isConnected = true,
      isInitialLoading = false,
      isReconnecting = false,
      isSending = false,
      isLoadingMore = false,
      isExpanded = false,
      isSoundOn = true,
      isTyping = false,
      isInputDisabled = false,
      error,
      onSendMessage,
      onReachEnd,
      onRetryLoad,
      onExpandToggle,
      onSoundToggle,
      onRestart,
      onClose,
      onRevise,
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState("");

    const [
      productDetailsDrawerState,
      openProductDetailsDrawer,
      closeProductDetailsDrawer,
    ] = useDialogState<{ product: ChatBotUIProduct | null }>({
      isOpen: false,
      data: { product: null },
    });

    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const lastMessageRef = useRef<HTMLDivElement | null>(null);
    const secondToLastMessageRef = useRef<HTMLDivElement | null>(null);
    const outgoingMessageRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const isLoadingMoreRef = useRef(false);
    const hasInitialScrolledRef = useRef(false);
    const floatingAlertManagerRef = useRef<FloatingAlertManagerHandle | null>(
      null
    );

    const typingDelayMs = useMemo(
      () => Math.random() * 1500 + 500,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isTyping]
    );

    const areSuggestionsVisible = suggestions.length > 0 && !isInitialLoading;

    const scrollToLastMessage = useCallback(() => {
      const scrollToLast = () => {
        lastMessageRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      };

      if (!outgoingMessageRef.current || !messagesContainerRef.current) {
        scrollToLast();
        return;
      }

      // If the last outgoing message is too high, scroll directly to the last message
      if (
        outgoingMessageRef.current.clientHeight >
        messagesContainerRef.current.clientHeight - 200
      ) {
        scrollToLast();
        return;
      }

      // If there is more than one incoming message after the last outgoing message, scroll to the last message
      if (secondToLastMessageRef.current !== outgoingMessageRef.current) {
        scrollToLast();
        return;
      }

      // Otherwise include the outgoing message as well
      outgoingMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, []);

    // Expose imperative methods
    useImperativeHandle(ref, () => ({
      clearInput: () => setInputValue(""),
      setInput: (value: string) => setInputValue(value),
      getInput: () => inputValue,
      scrollToLastMessage,
      focusInput: () => {
        inputRef.current?.focus();
      },
      enqueueAlert: (options) => {
        floatingAlertManagerRef.current?.enqueueAlert(options);
      },
    }));

    // Scroll to bottom on initial load
    useLayoutEffect(() => {
      const container = messagesContainerRef.current;
      const messagesEnd = messagesEndRef.current;

      if (
        !container ||
        messages.length === 0 ||
        isInitialLoading ||
        !messagesEnd
      ) {
        return;
      }

      // Initial scroll without animation
      if (!hasInitialScrolledRef.current) {
        hasInitialScrolledRef.current = true;
        messagesEnd.scrollIntoView({ behavior: "auto" });
      }
    }, [messages, isInitialLoading]);

    // Handle scroll for loading more messages
    useEffect(() => {
      const container = messagesContainerRef.current;
      if (!container || !onReachEnd) return () => {};

      const handleScroll = () => {
        if (
          container.scrollTop < 200 &&
          !isLoadingMore &&
          !isLoadingMoreRef.current
        ) {
          isLoadingMoreRef.current = true;
          onReachEnd();

          // Reset flag after a delay
          setTimeout(() => {
            isLoadingMoreRef.current = false;
          }, 500);
        }
      };

      container.addEventListener("scroll", handleScroll);

      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }, [onReachEnd, isLoadingMore]);

    const handleSendMessage = useCallback(() => {
      const text = inputValue.trim();

      if (!text) return;

      onSendMessage({ type: "text", text });
      setInputValue("");
      inputRef.current?.focus();
    }, [inputValue, onSendMessage]);

    const handleInputChange = useCallback((value: string) => {
      setInputValue(value);
    }, []);

    return (
      <div className="relative h-full min-h-0 w-full min-w-0">
        <MultiSectionLayout
          className="min-w-0 max-w-full overflow-x-hidden"
          classNames={{
            middle: "min-w-0",
            content: "min-w-0 overflow-x-hidden",
          }}
          header={
            <ChatHeader
              title={config.headerTitle}
              imageUrl={config.chatImageUrl}
              isExpanded={isExpanded}
              isSoundOn={isSoundOn}
              onExpandToggle={onExpandToggle}
              onSoundToggle={onSoundToggle}
              onRestart={onRestart}
              onClose={onClose}
            />
          }
          footer={<ChatFooter />}
        >
          {/* Chat Content */}
          <div className="font-secondary relative flex h-full w-full flex-col overflow-hidden bg-white">
            <FloatingAlertManager ref={floatingAlertManagerRef} />

            {isReconnecting && (
              <div className="flex shrink-0 items-center justify-center gap-2 border-b border-l-0 border-r-0 border-t-0 border-solid border-b-gray-100 bg-gray-50 px-4 py-2">
                <div className="h-3 w-3 animate-spin rounded-full border-2 border-gray-200 border-t-gray-500" />
                <span className="font-secondary text-xs text-gray-500">
                  Reconnecting…
                </span>
              </div>
            )}

            {/* Messages Container */}
            <div
              ref={messagesContainerRef}
              className="jugl__custom-scrollbar relative flex flex-1 flex-col gap-3 overflow-y-auto px-3 py-4"
            >
              {/* Initial Loading State */}
              {isInitialLoading && (
                <DelayedRender
                  isLoading
                  delayMs={500}
                  loadingContent={
                    <div className="flex flex-1 items-center justify-center">
                      <ProgressMessages
                        messages={[
                          "Preparing your chat",
                          "Setting things up",
                          "Almost there",
                        ]}
                        loop={false}
                        className="font-secondary text-sm text-[#707577]"
                      />
                    </div>
                  }
                />
              )}

              {/* Error State */}
              {error && !isInitialLoading && (
                <div className="mb-4 rounded-lg border border-solid border-red-200 bg-red-50 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-secondary m-0 text-sm font-medium text-red-800">
                        Failed to load messages
                      </p>
                      <p className="font-secondary m-0 mt-1 text-xs text-red-600">
                        {error.message ||
                          "An error occurred while loading messages."}
                      </p>
                    </div>
                    {onRetryLoad && (
                      <Button
                        onClick={onRetryLoad}
                        size="small"
                        color="tertiary"
                      >
                        Retry
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Loading More Indicator */}
              {isLoadingMore && messages.length > 0 && (
                <div className="flex justify-center py-2">
                  <span className="font-secondary rounded-full bg-white px-4 py-2 text-sm text-[#707577] shadow-md">
                    Loading older messages...
                  </span>
                </div>
              )}

              {!isInitialLoading && (
                <>
                  {/* Messages */}
                  {messages.map((message, index) => {
                    const isLast = index === messages.length - 1;
                    const isSecondToLast =
                      messages.length > 1 && index === messages.length - 2;
                    const isOutgoing = message.type === "outgoing";

                    if (message.type === "info") {
                      return (
                        <ChatBotInfoMessageComponent
                          ref={(el) => {
                            if (isOutgoing) outgoingMessageRef.current = el;
                            if (isLast) lastMessageRef.current = el;
                            if (isSecondToLast)
                              secondToLastMessageRef.current = el;
                          }}
                          key={message.id}
                          message={message}
                        />
                      );
                    }

                    const isIncoming = message.type === "incoming";

                    let boundOnRevise: (() => void) | undefined;

                    if (isIncoming && onRevise) {
                      const previousMessage = messages[index - 1];
                      const previousQuestion =
                        previousMessage?.type === "outgoing" &&
                        previousMessage.payload.type === "text"
                          ? previousMessage.payload.text
                          : undefined;

                      const agentAnswer =
                        message.payload.type === "text"
                          ? message.payload.text
                          : "";

                      if (previousQuestion) {
                        boundOnRevise = () =>
                          onRevise({
                            question: previousQuestion,
                            agentAnswer,
                          });
                      }
                    }

                    return (
                      <ChatBotMessageComponent
                        ref={(el) => {
                          if (isOutgoing) outgoingMessageRef.current = el;
                          if (isLast) lastMessageRef.current = el;
                          if (isSecondToLast)
                            secondToLastMessageRef.current = el;
                        }}
                        key={message.id}
                        message={message}
                        isLast={isLast}
                        userBubbleColor={config.userBubbleColor}
                        onSendMessage={onSendMessage}
                        onOpenProductDetailsDrawer={(product) =>
                          openProductDetailsDrawer({ product })
                        }
                        onRevise={boundOnRevise}
                      />
                    );
                  })}

                  <div ref={messagesEndRef} />

                  {/* Add some random delay to make it more natural */}
                  {isTyping && (
                    <DelayedRender
                      isLoading
                      delayMs={typingDelayMs}
                      loadingContent={
                        <TypingIndicatorBubble
                          userBubbleColor={config.userBubbleColor}
                          agentName={config.agentName}
                          agentImageUrl={config.agentImageUrl}
                        />
                      }
                    />
                  )}
                </>
              )}
            </div>

            {/* Input Area */}
            <div
              className={cx(
                "px-4",
                areSuggestionsVisible &&
                  "border-t border-b-0 border-l-0 border-r-0 border-solid border-[#F2F2F2]"
              )}
            >
              {areSuggestionsVisible && (
                <div className="flex flex-wrap gap-2.5 py-4">
                  {suggestions.map((suggestion, index) => (
                    <InteractiveContainer
                      key={+index}
                      className={cx(
                        "flex h-[34px] items-center justify-center overflow-hidden rounded-3xl px-[18px] transition-colors",
                        "border-grey-300 text-dark border border-solid bg-white hover:border-[#2C2E2E] hover:bg-[#2C2E2E] hover:text-white"
                      )}
                      title={suggestion}
                      onClick={() => {
                        onSendMessage({ type: "text", text: suggestion });
                      }}
                    >
                      <span className="font-secondary truncate text-[13px] font-medium">
                        {suggestion}
                      </span>
                    </InteractiveContainer>
                  ))}
                </div>
              )}
              <ChatInput
                ref={inputRef}
                value={inputValue}
                onChange={handleInputChange}
                onSend={handleSendMessage}
                placeholder={config.inputPlaceholder}
                textSize={config.inputTextSize}
                isDisabled={
                  !isConnected ||
                  isInitialLoading ||
                  isSending ||
                  isInputDisabled
                }
              />
            </div>
          </div>
        </MultiSectionLayout>
        <ChatBotProductDetailsDrawer
          isOpen={productDetailsDrawerState.isOpen}
          product={productDetailsDrawerState.data.product}
          onAddToCart={(product, quantity) => {
            onSendMessage({
              type: "add-to-cart",
              data: { product, quantity },
            });
          }}
          onClose={closeProductDetailsDrawer}
        />
      </div>
    );
  }
);

ChatBotUI.displayName = "ChatBotUI";
